import { FC, useContext, useEffect, useRef, useState } from "react";
import { CardSkeletons, ColSize, ErrorResults, RowSize } from "../../../../../shared/components";
import { Grid } from "@hdi-seguros/web-components.grid";
import { ItemNotFound } from "../../../../../shared/components/ItemNotFound";
import { InsurancePolicyNotFound } from "../../../../../assets";
import { UIContext } from "../../../../../shared/contexts";
import { ENDORSEMENTS_TYPES_VALUES, EndorsementsTypesValues } from "../../shared/enums/endorsements-types-values-enum";
import EmissionService from "../../../../../shared/services/emission";
import { ComponentEndorsements, GetEndorsementsListResponse } from "../../../../../shared/interfaces/services/endorsement-list-service";
import { EndorsementsCard } from "./components/EndorsementsElevatorsCard";
import './index.css';
import { ActionButton } from "@hdi-seguros/web-components.action-button";

interface EndorsementsTabProps {
    type: EndorsementsTypesValues;
    onEndorsementsProcessed: (message: string) => void;
}

export const EndorsementsTab: FC<EndorsementsTabProps> = ({type, onEndorsementsProcessed}) => {
    const gridRef = useRef();

    const { isLoading, showError, setIsLoading, setShowError } = useContext(UIContext);

    const [endorsementList, setEndorsementList] = useState<GetEndorsementsListResponse>({});
    const [endorsementComponent, setEndorsementComponent] = useState<Array<ComponentEndorsements>>(endorsementList.componentes || []);

    const [selectedEndorsements, setSelectedEndorsements] = useState<Array<ComponentEndorsements>>([]);
    const [buttonLoading, setButtonLoading] = useState(false);

    const handleSelectionChange = (component: ComponentEndorsements, isSelected: boolean) => {
        if(isSelected){
            setSelectedEndorsements((prev) => [...prev, component]);
        }else{
            setSelectedEndorsements((prev) => prev.filter((endorsement) => endorsement.operacion !== component.operacion));
        }
    };

    const fetchEndorsements = async () => {
        setIsLoading(true);
        try {
            if(type === ENDORSEMENTS_TYPES_VALUES.CALDERAS){
                const response = await EmissionService.getEndorsementsElevatorsBoilersList();
                if(response?.componentes) setEndorsementList(response);
            } else {
                const response = await EmissionService.getEndorsementsCars();
                if(response?.componentes) setEndorsementList(response);
            }
        } catch {
            setShowError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchEndorsements();
    }, []);

    useEffect(() => {
        if (endorsementList) {
            (gridRef.current as any).setPage(1);
        }
    }, [endorsementList, setEndorsementList]);

    const executeEndorsements = async () => {
        setButtonLoading(true);
        try {
            let results;
            if(type === ENDORSEMENTS_TYPES_VALUES.CALDERAS){
                results = await Promise.allSettled(
                    selectedEndorsements.map((endorsement) => 
                        EmissionService.postExecuteEndorsementsElevatorsBoilers(endorsement)
                        .then(() => ({ policyNumber: endorsement.poliza }))
                    )
                );
            }else{
                results = await Promise.allSettled(
                    selectedEndorsements.map((endorsement ) => 
                        EmissionService.postExecuteCarsEndorsements(endorsement)
                        .then(() => ({ policyNumber: endorsement.nroPoliza }))
                    )
                );
            }
            const successfulPolicies: string[] = [];
            const failedPolicies: string[] = [];

            results.forEach((result, index) => {
                
                if (result.status === "fulfilled") {
                    successfulPolicies.push(result.value.policyNumber);
                } else {
                    failedPolicies.push(selectedEndorsements[index].poliza);
                }
            });
            
            let successfullMessage = '';
            if(successfulPolicies.length > 0){
                successfullMessage = `Se procesaron exitosamente los siguientes endosos: ${successfulPolicies.join(', ')}. \n`; 
                if(failedPolicies.length > 0){
                    successfullMessage = successfullMessage + `No se pudieron procesar los siguientes endosos: ${failedPolicies.join(', ')}.`;
                }
                onEndorsementsProcessed(successfullMessage);
            }
                       
        } catch (error) {
        } finally {
            setButtonLoading(false);
        }
    }; 

    return (
        <div className="endorsements-tab">
            {
                showError 
                    ? <ErrorResults title="No se pudo cargar el listado de endosos" />
                    : (
                           <RowSize rowProps={{ gutter: [15, 15], className: 'endorsements-tab__grid-cards' }}> 
                                <div className="endorsements-tab__button-container">
                                    <ActionButton loading={buttonLoading} disabled={selectedEndorsements.length === 0} label="Procesar Selección" type="secondary" className="withholding-list__proccess-button btn-accent-outline-barbuss" onClick={executeEndorsements} />
                                </div>
                                <ColSize size={100}>
                                    <Grid
                                        list={(endorsementList?.componentes) || []} 
                                        setState={setEndorsementComponent} 
                                        name="endosos" 
                                        ref={gridRef}
                                    >
                                        <ColSize size={100}>
                                            {
                                                endorsementComponent?.length
                                                    ? endorsementComponent.map(( endorsement, index ) => (
                                                        <EndorsementsCard
                                                            key={index}
                                                            type={type}
                                                            onSelectionChange={handleSelectionChange}
                                                            keyCard={index}
                                                            componentEndorsement={endorsement}
                                                            loading={isLoading}
                                                        />
                                                    ))
                                                : isLoading
                                                ? (
                                                    <RowSize rowProps={{ gutter: [15, 15] }}>
                                                        <CardSkeletons />
                                                    </RowSize>
                                                ) :  <ItemNotFound textDescription="No se encontraron endosos de ascensores y calderas" imageUrl={InsurancePolicyNotFound} />
                                            }
                                        </ColSize>
                                    </Grid>
                                </ColSize>
                            </RowSize>
                    )
            }
        </div>
    )

}   
